import { BridgeInputMessage, BridgeOutputMessage } from '../bridgeBase';

/**
 * Enum defining the types of messages that can be exchanged between the
 * main thread and service worker for screen recording functionality.
 */
export enum ScreenRecordingMessageType {
  /** Initialize a new screen recording session */
  INITIALIZE = 'screen_recording_initialize',
  /** Save a chunk of recorded screen data */
  SAVE_CHUNK = 'screen_recording_save_chunk', 
  /** Clean up resources after recording is complete */
  CLEANUP = 'screen_recording_cleanup',
  /** Indicate an error occurred during recording */
  ERROR = 'screen_recording_error',
  /** Indicate a successful operation */
  SUCCESS = 'screen_recording_success'
}

/**
 * Interface defining metadata associated with a screen recording.
 */
export interface RecordingMetadata {
  /** Unique identifier for the assessment associated with this recording */
  assessmentUUID: string;
  /** Unique identifier for the user performing the recording */
  userUUID: string;
  /** Timestamp when the recording was created (milliseconds since epoch) */
  timestamp: number;
}

/**
 * Message sent to initialize a new screen recording session.
 * This prepares the service worker to receive recording chunks.
 */
export class ScreenRecordingInitializeMessage extends BridgeInputMessage {
  public readonly type = ScreenRecordingMessageType.INITIALIZE;
  
  /**
   * @param metadata Metadata associated with this recording session
   */
  constructor(public metadata: RecordingMetadata) {
    super();
  }
}

/**
 * Message sent to save a chunk of screen recording data.
 * Screen recordings are split into chunks to manage memory efficiently.
 */
export class ScreenRecordingSaveChunkMessage extends BridgeInputMessage {
  public readonly type = ScreenRecordingMessageType.SAVE_CHUNK;
  
  /**
   * @param chunkBlob Binary blob containing a portion of the screen recording
   * @param chunkNumber Sequential number of this chunk in the recording
   * @param metadata Metadata associated with this recording session
   */
  constructor(
    public chunkBlob: Blob,
    public chunkNumber: number,
    public metadata: RecordingMetadata
  ) {
    super();
  }
}

/**
 * Message sent to clean up resources after a recording session is complete.
 * This allows the service worker to perform any necessary finalization.
 */
export class ScreenRecordingCleanupMessage extends BridgeInputMessage {
  public readonly type = ScreenRecordingMessageType.CLEANUP;
  
  /**
   * @param metadata Metadata associated with this recording session
   */
  constructor(public metadata: RecordingMetadata) {
    super();
  }
}

/**
 * Message sent from the service worker to indicate an error occurred
 * during screen recording operations.
 */
export class ScreenRecordingErrorMessage extends BridgeOutputMessage {
  public readonly type = ScreenRecordingMessageType.ERROR;
  
  /**
   * @param errorCode Machine-readable error code
   * @param errorMessage Human-readable error message
   */
  constructor(
    public errorCode: string,
    public errorMessage: string
  ) {
    super();
  }
}

/**
 * Message sent from the service worker to indicate a successful
 * screen recording operation.
 */
export class ScreenRecordingSuccessMessage extends BridgeOutputMessage {
  public readonly type = ScreenRecordingMessageType.SUCCESS;
  
  /**
   * @param operationType The type of operation that succeeded (e.g., 'initialize', 'save', 'cleanup')
   * @param details Optional additional details about the successful operation
   */
  constructor(
    public operationType: string,
    public details?: any
  ) {
    super();
  }
}